import { useEffect, useState } from "react";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { Dumbbell, GitFork, BookOpen, RefreshCw, Plus, Trash2, Check, X, Loader2, Sparkles, Eye } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "rules", label: "Règles", Icon: GitFork },
  { key: "exercises", label: "Exercices", Icon: Dumbbell },
  { key: "knowledge", label: "Connaissances", Icon: BookOpen },
  { key: "updates", label: "Mises à jour", Icon: RefreshCw },
];

export default function CoachEngine() {
  const [tab, setTab] = useState("rules");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState([]);
  const [form, setForm] = useState({});
  const [srcText, setSrcText] = useState("");
  const [srcUrl, setSrcUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const fetchTab = () => {
    setLoading(true);
    if (tab === "updates") {
      api.get("/engine-proposals").then((r) => setProposals(r.data.proposals || [])).finally(() => setLoading(false));
    } else {
      api.get(`/engine/${tab}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
    }
  };
  useEffect(() => { setForm({}); fetchTab(); /* eslint-disable-next-line */ }, [tab]);

  const create = async () => {
    setBusy(true);
    try {
      let body = { ...form };
      if (tab === "exercises") body.goals = (form.goals || "").split(",").map((s) => s.trim()).filter(Boolean);
      await api.post(`/engine/${tab}`, body);
      toast.success("Ajouté au moteur");
      setForm({}); fetchTab();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet élément du moteur ?")) return;
    try { await api.delete(`/engine/${tab}/${id}`); toast.success("Supprimé"); fetchTab(); }
    catch { toast.error("Erreur"); }
  };

  const ingest = async () => {
    if (!srcText.trim() && !srcUrl.trim()) { toast.error("Colle un texte ou une URL"); return; }
    setBusy(true);
    try {
      const r = await api.post("/engine/ingest-source", { text: srcText.trim() || null, url: srcUrl.trim() || null });
      toast.success(r.data.message);
      setSrcText(""); setSrcUrl(""); fetchTab();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const runWatch = async () => {
    setBusy(true);
    try { const r = await api.post("/engine/watch/run"); toast.success(r.data.message); fetchTab(); }
    catch { toast.error("Veille indisponible"); } finally { setBusy(false); }
  };

  const acceptP = async (id) => { try { await api.post(`/engine/proposals/${id}/accept`); toast.success("Intégré au moteur"); fetchTab(); } catch { toast.error("Erreur"); } };
  const dismissP = async (id) => { try { await api.post(`/engine/proposals/${id}/dismiss`); fetchTab(); } catch { toast.error("Erreur"); } };

  const runPreview = async (payload) => {
    setPreviewing(true); setPreview({ loading: true });
    try {
      const r = await api.post("/engine/preview-rule", payload);
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Aperçu indisponible"); setPreview(null); }
    finally { setPreviewing(false); }
  };
  const previewDraft = () => runPreview({ name: form.name, domain: form.domain || "10km", action: form.action, conditions: form.conditions, population: form.population || "tous" });
  const previewProposal = (p) => runPreview({ proposal_id: p.proposal_id });

  const field = (k, ph, cls = "") => (
    <input data-testid={`engine-form-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      placeholder={ph} className={`border border-border rounded-sm px-2 py-1.5 text-sm ${cls}`} />
  );

  return (
    <CoachLayout>
      <div className="fade-up">
        <h1 className="text-2xl font-medium">Moteur</h1>
        <p className="mt-2 text-sm text-obsidian/50">Entraîne MAI en autonomie : ajoute/édite des règles, exercices et connaissances. Les nouveautés issues de sources ou de la veille passent par ta validation avant d'entrer dans le moteur.</p>

        <div className="mt-6 flex flex-wrap gap-2">
          {TABS.map(({ key, label, Icon }) => (
            <button key={key} data-testid={`engine-tab-${key}`} onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 rounded-sm border px-4 py-2 text-sm transition-[background-color,border-color] ${tab === key ? "border-obsidian bg-obsidian text-white" : "border-border bg-white"}`}>
              <Icon size={15} strokeWidth={1.6} /> {label}
            </button>
          ))}
        </div>

        {/* ---- CRÉATION ---- */}
        {tab === "rules" && (
          <div className="mt-6 bg-white border border-dashed border-border p-4" data-testid="engine-create-rule">
            <div className="text-sm font-medium mb-2 flex items-center gap-1.5"><Plus size={14} /> Nouvelle règle</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {field("name", "Nom de la règle")}
              {field("domain", "Domaine (endurance, sécurité, progression…)")}
              {field("conditions", "SI … (conditions)")}
              {field("action", "ALORS … (action)")}
              {field("population", "Population (tous, débutant…)")}
              {field("priority", "Priorité 1-5")}
            </div>
            <div className="mt-2 flex gap-2">
              <button data-testid="engine-create-rule-btn" onClick={create} disabled={busy || !form.name} className="rounded-sm bg-obsidian text-white px-4 py-1.5 text-sm inline-flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Ajouter au moteur</button>
              <button data-testid="engine-preview-rule-btn" onClick={previewDraft} disabled={previewing || !form.action} className="rounded-sm border border-obsidian px-4 py-1.5 text-sm inline-flex items-center gap-1.5 disabled:opacity-50">{previewing ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Prévisualiser l'effet</button>
            </div>
          </div>
        )}
        {tab === "exercises" && (
          <div className="mt-6 bg-white border border-dashed border-border p-4" data-testid="engine-create-exercise">
            <div className="text-sm font-medium mb-2 flex items-center gap-1.5"><Plus size={14} /> Nouvel exercice</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {field("name", "Nom (ex: Fartlek 30/30)")}
              {field("goals", "Objectifs (vma, endurance, seuil…) séparés par virgule")}
              {field("equipment", "Matériel (optionnel)")}
              {field("instructions", "Instructions", "sm:col-span-2")}
            </div>
            <button data-testid="engine-create-exercise-btn" onClick={create} disabled={busy || !form.name} className="mt-2 rounded-sm bg-obsidian text-white px-4 py-1.5 text-sm inline-flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Ajouter au moteur</button>
          </div>
        )}
        {tab === "knowledge" && (
          <div className="mt-6 bg-white border border-dashed border-border p-4" data-testid="engine-create-knowledge">
            <div className="text-sm font-medium mb-2 flex items-center gap-1.5"><Plus size={14} /> Nouvelle connaissance</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {field("name", "Titre")}
              {field("question", "Question / sujet")}
              {field("conclusion", "Conclusion", "sm:col-span-2")}
              {field("sources", "Source(s)")}
            </div>
            <button data-testid="engine-create-knowledge-btn" onClick={create} disabled={busy || !form.name} className="mt-2 rounded-sm bg-obsidian text-white px-4 py-1.5 text-sm inline-flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Ajouter au moteur</button>
          </div>
        )}

        {/* ---- MISES À JOUR ---- */}
        {tab === "updates" && (
          <div className="mt-6 space-y-4">
            <div className="bg-white border border-border p-5" data-testid="engine-source">
              <div className="text-sm font-medium flex items-center gap-1.5"><Sparkles size={15} className="text-sage" /> Apprendre depuis une source</div>
              <p className="mt-1 text-xs text-obsidian/50">Colle le texte d'un article/étude ou une URL. MAI en extrait des propositions que tu valides ensuite.</p>
              <input data-testid="engine-src-url" value={srcUrl} onChange={(e) => setSrcUrl(e.target.value)} placeholder="https://… (optionnel)" className="mt-3 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
              <textarea data-testid="engine-src-text" rows={4} value={srcText} onChange={(e) => setSrcText(e.target.value)} placeholder="…ou colle ici le texte de la source" className="mt-2 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
              <div className="mt-2 flex gap-2">
                <button data-testid="engine-ingest-btn" onClick={ingest} disabled={busy} className="rounded-sm bg-obsidian text-white px-4 py-1.5 text-sm inline-flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Analyser la source</button>
                <button data-testid="engine-watch-btn" onClick={runWatch} disabled={busy} className="rounded-sm border border-obsidian px-4 py-1.5 text-sm inline-flex items-center gap-1.5 disabled:opacity-50"><RefreshCw size={13} /> Lancer une veille</button>
              </div>
              <p className="mt-2 text-[11px] text-obsidian/40">Une veille automatique s'exécute aussi chaque mois. Rien n'est intégré sans ta validation.</p>
            </div>

            <div>
              <div className="text-sm font-medium mb-2">Propositions à valider ({proposals.length})</div>
              {loading ? <div className="pulse-organic w-3 h-3 rounded-full bg-obsidian" /> : proposals.length === 0 ? (
                <p className="text-sm text-obsidian/40">Aucune proposition en attente.</p>
              ) : proposals.map((p) => (
                <div key={p.proposal_id} data-testid={`proposal-${p.proposal_id}`} className="bg-white border border-border p-4 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide bg-secondary px-2 py-0.5 rounded">{p.kind === "rule" ? "Règle" : "Exercice"}</span>
                    <span className="text-[10px] uppercase tracking-wide bg-sage/15 text-sage px-2 py-0.5 rounded">{p.source}</span>
                    <span className="font-medium text-sm">{p.payload?.name}</span>
                  </div>
                  {p.kind === "rule" ? (
                    <div className="mt-1.5 text-xs text-obsidian/60"><span className="font-semibold">SI</span> {p.payload?.conditions || "—"} <span className="font-semibold">→</span> {p.payload?.action || p.payload?.description}</div>
                  ) : (
                    <div className="mt-1.5 text-xs text-obsidian/60">Objectifs : {(p.payload?.goals || []).join(", ") || "—"} · {p.payload?.instructions}</div>
                  )}
                  {p.rationale && <div className="mt-1 text-[11px] text-obsidian/45 italic">{p.rationale}</div>}
                  {p.payload?.source && <div className="mt-0.5 text-[11px] text-obsidian/40">Source : {p.payload.source}</div>}
                  <div className="mt-3 flex gap-2">
                    <button data-testid={`proposal-accept-${p.proposal_id}`} onClick={() => acceptP(p.proposal_id)} className="rounded-sm bg-obsidian text-white px-3.5 py-1.5 text-sm inline-flex items-center gap-1.5"><Check size={13} /> Intégrer</button>
                    {p.kind === "rule" && <button data-testid={`proposal-preview-${p.proposal_id}`} onClick={() => previewProposal(p)} disabled={previewing} className="rounded-sm border border-obsidian px-3.5 py-1.5 text-sm inline-flex items-center gap-1.5 disabled:opacity-50">{previewing ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Aperçu</button>}
                    <button data-testid={`proposal-dismiss-${p.proposal_id}`} onClick={() => dismissP(p.proposal_id)} className="rounded-sm border border-border px-3.5 py-1.5 text-sm inline-flex items-center gap-1.5"><X size={13} /> Ignorer</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---- LISTE ---- */}
        {tab !== "updates" && (
          <div className="mt-4">
            <div className="flex items-center gap-2 text-sm text-obsidian/50 mb-3"><span className="font-display text-xl font-medium tabnum text-obsidian">{items.length}</span> éléments</div>
            {loading ? <div className="pulse-organic w-3 h-3 rounded-full bg-obsidian" /> : (
              <div className="space-y-2">
                {tab === "rules" && items.map((r) => (
                  <div key={r.rule_id} data-testid={`engine-rule-${r.rule_id}`} className="bg-white border border-border p-4">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs bg-obsidian text-white px-1.5 py-0.5 rounded-sm">{r.rule_id}</span>
                      <span className="font-medium text-sm">{r.name}</span>
                      <button data-testid={`engine-delete-${r.rule_id}`} onClick={() => del(r.rule_id)} className="ml-auto p-1 text-obsidian/40 hover:text-destructive"><Trash2 size={14} /></button>
                    </div>
                    <div className="mt-2 text-xs text-obsidian/60"><span className="font-semibold">SI</span> {r.conditions} <span className="font-semibold">→</span> {r.action}</div>
                    <div className="mt-1 text-[11px] text-obsidian/40">Priorité {r.priority} · {r.population} · {r.origin || r.confidence}</div>
                  </div>
                ))}
                {tab === "exercises" && items.map((e) => (
                  <div key={e.exercise_id} data-testid={`engine-ex-${e.exercise_id}`} className="bg-white border border-border p-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{e.name}</span>
                      <button data-testid={`engine-delete-${e.exercise_id}`} onClick={() => del(e.exercise_id)} className="ml-auto p-1 text-obsidian/40 hover:text-destructive"><Trash2 size={14} /></button>
                    </div>
                    <div className="mt-1 text-xs text-obsidian/50">Objectifs : {(e.goals || []).join(", ") || "—"} · Matériel : {(e.equipment || []).join(", ") || "aucun"}</div>
                    {e.instructions && <div className="mt-1 text-xs text-obsidian/40">{e.instructions}</div>}
                  </div>
                ))}
                {tab === "knowledge" && items.map((k) => (
                  <div key={k.knowledge_id} data-testid={`engine-k-${k.knowledge_id}`} className="bg-white border border-border p-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{k.name}</span>
                      <button data-testid={`engine-delete-${k.knowledge_id}`} onClick={() => del(k.knowledge_id)} className="ml-auto p-1 text-obsidian/40 hover:text-destructive"><Trash2 size={14} /></button>
                    </div>
                    {k.question && <div className="mt-1 text-xs text-obsidian/50 italic">{k.question}</div>}
                    {k.conclusion && <p className="mt-1.5 text-sm text-obsidian/80">{k.conclusion}</p>}
                    {k.sources && <div className="mt-1 text-[11px] text-obsidian/40">Source : {k.sources}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ---- MODALE APERÇU AVANT / APRÈS ---- */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-obsidian/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="preview-modal" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="text-lg font-medium flex items-center gap-2"><Eye size={18} /> Aperçu de l'effet {preview.rule?.name ? `· ${preview.rule.name}` : ""}</div>
              <button data-testid="preview-close" onClick={() => setPreview(null)} className="p-1 text-obsidian/40 hover:text-obsidian"><X size={18} /></button>
            </div>
            {preview.loading ? (
              <div className="py-16 flex flex-col items-center gap-3 text-obsidian/50"><Loader2 size={22} className="animate-spin" /> MAI simule une semaine type avant / après…</div>
            ) : (
              <>
                {preview.impact && (
                  <div className="mt-4 rounded-lg border border-sage/30 bg-sage/8 p-4 text-sm text-obsidian/80" data-testid="preview-impact">
                    <span className="font-semibold text-sage">Ce que ça change : </span>{preview.impact}
                  </div>
                )}
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  {[["Avant", preview.before], ["Après", preview.after]].map(([title, list], i) => (
                    <div key={i} className={`rounded-lg border p-3 ${i === 1 ? "border-obsidian" : "border-border"}`}>
                      <div className={`text-xs uppercase tracking-wide font-semibold mb-2 ${i === 1 ? "text-obsidian" : "text-obsidian/40"}`}>{title}</div>
                      <div className="space-y-2">
                        {(list || []).map((s, j) => (
                          <div key={j} className="rounded-md bg-secondary/60 px-3 py-2">
                            <div className="text-sm font-medium">{s.day} · {s.title} <span className="text-obsidian/40 font-normal">({s.duration_min} min)</span></div>
                            {s.note && <div className="text-[11px] text-obsidian/50 mt-0.5">{s.note}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-obsidian/40">Semaine type simulée sur un coureur représentatif. Rien n'est modifié tant que tu n'as pas intégré la règle.</p>
              </>
            )}
          </div>
        </div>
      )}
    </CoachLayout>
  );
}
